import { useEffect } from "react";
import { DEFAULT_OG_IMAGE, SITE_NAME, absoluteUrl } from "@/lib/site";

type SeoHeadProps = {
  title: string;
  description: string;
  canonicalPath?: string;
  image?: string;
  robots?: string;
  type?: "website" | "product" | "article";
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
};

function ensureMeta(selector: string, attributes: Record<string, string>) {
  let element = document.head.querySelector<HTMLMetaElement>(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    element?.setAttribute(key, value);
  });
}

function ensureLinkRel(rel: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

  if (!element) {
    element = document.createElement("link");
    element.rel = rel;
    document.head.appendChild(element);
  }

  element.href = href;
}

export function SeoHead({
  title,
  description,
  canonicalPath = "/",
  image,
  robots = "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1",
  type = "website",
  jsonLd,
}: SeoHeadProps) {
  useEffect(() => {
    const canonical = absoluteUrl(canonicalPath);
    const socialImage = absoluteUrl(image || DEFAULT_OG_IMAGE);
    const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

    document.title = fullTitle;

    ensureMeta('meta[name="description"]', { name: "description", content: description });
    ensureMeta('meta[name="robots"]', { name: "robots", content: robots });
    ensureMeta('meta[property="og:title"]', { property: "og:title", content: fullTitle });
    ensureMeta('meta[property="og:description"]', { property: "og:description", content: description });
    ensureMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    ensureMeta('meta[property="og:type"]', { property: "og:type", content: type });
    ensureMeta('meta[property="og:site_name"]', { property: "og:site_name", content: SITE_NAME });
    ensureMeta('meta[property="og:image"]', { property: "og:image", content: socialImage });
    ensureMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    ensureMeta('meta[name="twitter:title"]', { name: "twitter:title", content: fullTitle });
    ensureMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
    ensureMeta('meta[name="twitter:image"]', { name: "twitter:image", content: socialImage });
    ensureLinkRel("canonical", canonical);

    const existingScripts = Array.from(
      document.head.querySelectorAll<HTMLScriptElement>('script[data-seo-jsonld="true"]'),
    );
    existingScripts.forEach((script) => script.remove());

    const payloads = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : [];
    payloads.forEach((payload) => {
      const script = document.createElement("script");
      script.type = "application/ld+json";
      script.dataset.seoJsonld = "true";
      script.text = JSON.stringify(payload);
      document.head.appendChild(script);
    });

    return () => {
      Array.from(document.head.querySelectorAll('script[data-seo-jsonld="true"]')).forEach((script) => script.remove());
    };
  }, [canonicalPath, description, image, jsonLd, robots, title, type]);

  return null;
}
